import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { BillRequest, ConvertResult, SupplierBillRequestService } from '../../../services/supplierBillRequest.service';
import { GoodsReceiptService } from '../../../services/goodsReceipt.service';
import { AccountService } from '../../../services/account.service';
import { Account } from '../../../interfaces/accounting.interface';
import { GoodsReceiptLine } from '../../../interfaces/procurement/goodsReceipt.interface';
import { AccountAutocomplete } from '../../common/AccountAutocomplete';
import { leafAccounts } from '../../../utils/postingAccounts';
import { useToast } from '../../../context/ToastContext';
import { formatMoney } from '../../../utils/format';
import ScrollableTable from '../../common/ScrollableTable';

// ---- F8: convert a supplier bill request into a draft supplier bill through the existing bill services ----
interface GrnLineRow { line: GoodsReceiptLine; grnNumber: string; }
interface ServiceLine { accountId: string; description: string; amount: string; }

const plusDays = (iso: string, days: number) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const ConvertBillRequestDialog: React.FC<{
  institutionId: string; request: BillRequest; onClose: () => void; onConverted: (r: ConvertResult) => void;
}> = ({ institutionId, request, onClose, onConverted }) => {
  const toast = useToast();
  const poMode = !!request.purchaseOrderId;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [invoiceRef, setInvoiceRef] = useState(request.supplierInvoiceNo);
  const [billDate, setBillDate] = useState(request.invoiceDate);
  const [dueDate, setDueDate] = useState(plusDays(request.invoiceDate, 30));
  const [grnLines, setGrnLines] = useState<GrnLineRow[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // G5a: this form prices in BDT; a foreign-currency PO is billed from the PO bill page (the server refuses it here too).
  const [foreignCurrency, setForeignCurrency] = useState<string | null>(null);
  const [billed, setBilled] = useState<Set<string>>(new Set());
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [lines, setLines] = useState<ServiceLine[]>([
    { accountId: '', description: request.description ?? `Supplier invoice ${request.supplierInvoiceNo}`, amount: String(request.amount) },
  ]);

  useEffect(() => {
    const load = async () => {
      try {
        if (poMode) {
          const res = await GoodsReceiptService.listByInstitution(0, 500);
          const posted = res.content.filter(g => g.purchaseOrderId === request.purchaseOrderId && g.status === 'POSTED');
          const fc = posted.find(g => g.currencyCode && g.currencyCode !== 'BDT');
          if (fc) {
            setForeignCurrency(fc.currencyCode as string);
            return;
          }
          const [full, covered] = await Promise.all([
            Promise.all(posted.map(g => GoodsReceiptService.getById(g.id))),
            SupplierBillRequestService.coveredGrnLines(institutionId, request.id),
          ]);
          const rows = full.flatMap(g => (g.lines ?? []).map(l => ({ line: l, grnNumber: g.grnNumber })));
          const alreadyBilled = new Set(covered);
          setGrnLines(rows);
          setBilled(alreadyBilled);
          // Receipt lines already on a live bill can't be billed again; leave them unticked and locked.
          setSelected(new Set(rows.filter(r => !alreadyBilled.has(r.line.id)).map(r => r.line.id)));
          setPrices(Object.fromEntries(rows.map(r => [r.line.id, String(r.line.unitPrice)])));
        } else {
          setAccounts((await AccountService.getAllAccounts()) ?? []);
        }
      } catch (e) {
        toast.showApiError(e, 'Failed to load the bill form');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const postingAccounts = useMemo(() => leafAccounts(accounts), [accounts]);
  const poSubtotal = grnLines.filter(r => selected.has(r.line.id))
    .reduce((a, r) => a + Number(prices[r.line.id] || 0) * r.line.receivedQty, 0);
  const svcSubtotal = lines.reduce((a, l) => a + (Number(l.amount) > 0 ? Number(l.amount) : 0), 0);
  const valid = billDate !== '' && dueDate !== '' && (poMode
    ? selected.size > 0 && grnLines.filter(r => selected.has(r.line.id)).every(r => Number(prices[r.line.id]) > 0)
    : lines.length > 0 && lines.every(l => l.accountId && Number(l.amount) > 0));

  const setLine = (i: number, patch: Partial<ServiceLine>) => setLines(ls => ls.map((l, k) => (k === i ? { ...l, ...patch } : l)));

  const submit = async () => {
    setSaving(true);
    try {
      const result = await SupplierBillRequestService.convert(institutionId, request.id, poMode ? {
        poBill: {
          purchaseOrderId: request.purchaseOrderId as string, supplierInvoiceRef: invoiceRef, billDate, dueDate, institutionId,
          lines: grnLines.filter(r => selected.has(r.line.id)).map(r => ({
            goodsReceiptLineId: r.line.id, quantity: r.line.receivedQty, unitPrice: Number(prices[r.line.id]),
          })),
        },
      } : {
        serviceBill: {
          institutionId, supplierId: request.supplierId, supplierInvoiceRef: invoiceRef, billDate, dueDate,
          lines: lines.map(l => ({ accountId: l.accountId, description: l.description || undefined, amount: Number(l.amount) })),
        },
      });
      onConverted(result);
    } catch (e) {
      toast.showApiError(e, 'Failed to convert the bill request');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={() => !saving && onClose()} maxWidth="md" fullWidth data-help-page="procurement.billConvertForm.page">
      <DialogTitle>Convert {request.requestNo} to a supplier bill</DialogTitle>
      <DialogContent>
        {loading ? <CircularProgress /> : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2">
              {request.supplierName} invoice {request.supplierInvoiceNo}, {formatMoney(request.amount)}.
              {poMode ? ` Against PO ${request.poNumber ?? ''}: bill the received lines.` : ' No PO: a service bill debiting the accounts you choose.'}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <TextField data-help="procurement.billConvertForm.field.invoiceRef" label="Supplier invoice ref" value={invoiceRef} onChange={e => setInvoiceRef(e.target.value)} sx={{ flex: '1 1 200px', maxWidth: 280 }} />
              <TextField data-help="procurement.billConvertForm.field.billDate" label="Bill date" type="date" value={billDate} onChange={e => setBillDate(e.target.value)}
                InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 160px', maxWidth: 200 }} />
              <TextField data-help="procurement.billConvertForm.field.dueDate" label="Due date" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
                InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 160px', maxWidth: 200 }} />
            </Box>
            {poMode && foreignCurrency ? (
              <Alert severity="error">
                PO {request.poNumber ?? ''} is in {foreignCurrency}: convert foreign-currency bill requests from the PO bill page
                (Procurement &gt; Supplier bills &gt; New, choose this PO), then reject or close this request.
              </Alert>
            ) : poMode ? (grnLines.length === 0 ? (
              <Alert severity="warning">No posted goods receipt for this PO yet. Receive the goods first, then convert.</Alert>
            ) : grnLines.every(r => billed.has(r.line.id)) ? (
              <Alert severity="warning">Every received line on this PO is already billed. Receive more goods, or reject this request.</Alert>
            ) : (
              <ScrollableTable>
                <Table size="small" data-help-section="procurement.billConvert.lines">
                  <TableHead><TableRow>
                    <TableCell padding="checkbox" data-help="procurement.billConvert.col.select" /><TableCell data-help="procurement.billConvert.col.grn">GRN</TableCell><TableCell data-help="procurement.billConvert.col.item">Item</TableCell>
                    <TableCell data-help="procurement.billConvert.col.qty" align="right">Received</TableCell><TableCell data-help="procurement.billConvert.col.price">Unit price</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {grnLines.map(r => (
                      <TableRow key={r.line.id}>
                        <TableCell padding="checkbox">
                          <Checkbox data-help="procurement.billConvert.action.select" checked={selected.has(r.line.id)} disabled={billed.has(r.line.id)}
                            inputProps={{ 'aria-label': `Bill ${r.grnNumber} line` }} onChange={(_, c) => setSelected(s => {
                            const n = new Set(s); if (c) n.add(r.line.id); else n.delete(r.line.id); return n;
                          })} />
                        </TableCell>
                        <TableCell>{r.grnNumber}</TableCell>
                        <TableCell>
                          {r.line.itemName ?? r.line.itemCode ?? '-'}
                          {billed.has(r.line.id) && <Typography variant="caption" color="text.secondary" display="block">Already billed</Typography>}
                        </TableCell>
                        <TableCell align="right">{r.line.receivedQty}</TableCell>
                        <TableCell>
                          <TextField data-help="procurement.billConvertForm.field.price" size="small" type="number" value={prices[r.line.id] ?? ''} sx={{ maxWidth: 140 }} disabled={billed.has(r.line.id)}
                            onChange={e => setPrices(p => ({ ...p, [r.line.id]: e.target.value }))} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollableTable>
            )) : (
              <>
                {lines.map((l, i) => (
                  <Box key={i} sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
                    <Box sx={{ flex: '1 1 240px', maxWidth: 340 }} data-help="procurement.billConvertForm.field.account">
                      <AccountAutocomplete accounts={postingAccounts} value={l.accountId} label="Debit account" required
                        onChange={v => setLine(i, { accountId: v })} />
                    </Box>
                    <TextField data-help="procurement.billConvertForm.field.description" label="Description" value={l.description} onChange={e => setLine(i, { description: e.target.value })}
                      sx={{ flex: '1 1 200px', maxWidth: 300 }} />
                    <TextField data-help="procurement.billConvertForm.field.amount" label="Amount" type="number" value={l.amount} onChange={e => setLine(i, { amount: e.target.value })}
                      sx={{ flex: '1 1 120px', maxWidth: 160 }} />
                    <IconButton data-help="procurement.billConvert.action.remove" aria-label="Remove line" disabled={lines.length === 1}
                      onClick={() => setLines(ls => ls.filter((_x, k) => k !== i))}><DeleteIcon /></IconButton>
                  </Box>
                ))}
                <Box><Button data-help="procurement.billConvert.addLine" startIcon={<AddIcon />} onClick={() => setLines(ls => [...ls, { accountId: '', description: '', amount: '' }])}>Add line</Button></Box>
              </>
            )}
            <Typography variant="body2">
              Bill subtotal {formatMoney(poMode ? poSubtotal : svcSubtotal)}; requested {formatMoney(request.amount)}.
              The bill follows the usual approval rules.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button data-help="procurement.billConvertForm.cancel" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button data-help="procurement.billConvert.create" variant="contained" disabled={loading || saving || !valid || !!foreignCurrency} onClick={submit}>Create bill</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConvertBillRequestDialog;
// ---- end F8 ----
